import { useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { useMagasin } from '../lib/client/hooks.ts'
import { Sheet } from './Sheet.tsx'

/**
 * La pastille de synchronisation.
 *
 * TROIS RÈGLES, et la première est la plus importante.
 *
 * 1. RIEN quand tout est synchronisé. L'absence EST le signal. Une coche
 *    verte permanente est du bruit qu'on apprend à ne plus voir, et qui ne
 *    dit donc plus rien le jour où ça compte.
 * 2. Gris quand il y a de l'attente. Être en attente n'est PAS un problème :
 *    c'est le fonctionnement normal d'un local-first. Colorer serait crier.
 * 3. Ambre seulement en échec — là, c'est actionnable. La règle de couleur de
 *    l'app s'applique aussi ici : la couleur suit l'actionnabilité.
 *
 * Et elle est INSPECTABLE : une pastille qu'on ne peut pas ouvrir est une
 * affirmation. On l'ouvre, et on lit la file en français, pas des noms
 * d'opération.
 */
export function PastilleSynchro() {
  const { enAttente, echec, conflits } = useMagasin()
  const [ouverte, setOuverte] = useState(false)

  if (enAttente === 0 && conflits.length === 0) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOuverte(true)}
        className={`pressable tnum rounded-full px-2.5 py-1 text-meta ${
          echec ? 'bg-bientot-soft text-bientot-ink' : 'text-muted'
        }`}
      >
        {echec ? `${enAttente} à renvoyer` : `↑ ${enAttente}`}
      </button>

      <Sheet ouverte={ouverte} onFermer={() => setOuverte(false)} titre="Synchronisation">
        {conflits.length > 0 && (
          <section className="mb-5">
            <h3 className="mb-2 text-label uppercase text-muted">Ajustements</h3>
            {conflits.map((c) => (
              <div key={c.opId} className="mb-2 rounded-sm bg-bientot-soft px-3 py-2">
                <p className="text-body text-bientot-ink">{c.message}</p>
                <button
                  type="button"
                  onClick={() => magasin.ecarterConflit(c.opId)}
                  className="pressable mt-1 text-meta text-muted underline underline-offset-2"
                >
                  D'accord
                </button>
              </div>
            ))}
          </section>
        )}

        <h3 className="mb-2 text-label uppercase text-muted">
          {enAttente} changement{enAttente > 1 ? 's' : ''} en attente
        </h3>
        <ul className="mb-4">
          {magasin.fileLisible().map((e) => (
            <li key={e.opId} className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="min-w-0 flex-1 truncate text-body text-ink">{e.texte}</span>
              <span className="tnum shrink-0 text-meta text-muted">{ilYA(e.le)}</span>
            </li>
          ))}
          {enAttente === 0 && <li className="text-body text-muted">Rien en attente.</li>}
        </ul>

        <button
          type="button"
          onClick={() => void magasin.synchroniser()}
          className="pressable mb-4 h-11 w-full rounded-sm bg-surface-sunk text-body text-ink"
        >
          Réessayer maintenant
        </button>
      </Sheet>
    </>
  )
}

function ilYA(t: number): string {
  const min = Math.round((Date.now() - t) / 60_000)
  if (min < 1) return "à l'instant"
  if (min < 60) return `il y a ${min} min`
  const h = Math.round(min / 60)
  return h < 24 ? `il y a ${h} h` : `il y a ${Math.round(h / 24)} j`
}
